import {
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import React, { FunctionComponent } from 'react'
import { createPortal } from 'react-dom'
import { SaleStatus } from '../../../models/sale.constants'

export type DismissableSaleStatus =
  | SaleStatus.LAYBY_SALE
  | SaleStatus.ACCOUNT_SALE
  | SaleStatus.AWAITING_PICKUP
  | SaleStatus.AWAITING_DISPATCH

export interface DismissSaleModalProps {
  saleStatus: DismissableSaleStatus
  onCancel: () => void
  onConfirm: () => void
}

export const DismissSaleModal: FunctionComponent<DismissSaleModalProps> = ({
  saleStatus: status,
  onCancel,
  onConfirm,
}) => {
  const t = useT()

  const getModalContent = (saleStatus: DismissableSaleStatus): string => {
    let content: string = ''

    switch (saleStatus) {
      case SaleStatus.LAYBY_SALE:
        content = t(
          'To keep these changes, continue the sale to accept payment or put it back on layby/layaway.',
          { _context: 'Sale dismissal confirmation modal for layby/layway.' }
        )
        break
      case SaleStatus.ACCOUNT_SALE:
        content = t(
          'To keep these changes, continue the sale to accept payment or put it back on account.',
          { _context: 'Sale dismissal confirmation modal for on account' }
        )
        break
      case SaleStatus.AWAITING_PICKUP:
      case SaleStatus.AWAITING_DISPATCH:
        content = t(
          'To keep these changes, continue the sale to accept payment or save this sale with fulfillments.',
          {
            _context:
              'Sale dismissal confirmation modal for awaiting pickup or dispatch',
          }
        )
        break
    }

    return content
  }

  return createPortal(
    <Modal onClose={onCancel} size="small">
      <ModalHeader>
        {t('There are unsaved changes', {
          _context: 'Dismiss sale confirmation modal header',
        })}
      </ModalHeader>
      <ModalContent>{getModalContent(status)}</ModalContent>
      <ModalActions>
        <div className="vd-btn-group">
          <Button
            variant="supplementary"
            onClick={onCancel}
            data-testid="continue-sale-button"
          >
            {t('Continue sale', {
              _context: 'Dismiss sale modal cancel button',
            })}
          </Button>
          <Button variant="go" onClick={onConfirm} autoFocus>
            {t('Discard changes', {
              _context: 'Dismiss sale modal confirm button',
            })}
          </Button>
        </div>
      </ModalActions>
    </Modal>,
    document.body
  )
}
